import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Edit, ExternalLink, Pin, X, LayoutDashboard, Users, GraduationCap, Award, Newspaper, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const NAV_ITEMS = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/trainings", icon: GraduationCap, label: "Trainings" },
  { to: "/admin/participants", icon: Users, label: "Participants" },
  { to: "/admin/certificates", icon: Award, label: "Certificates" },
  { to: "/admin/news", icon: Newspaper, label: "News" },
];

const EMPTY_FORM = {
  title: "",
  description: "",
  url: "",
  image_url: "",
  source: "ShansoAI"
};

export default function AdminNews() {
  const [pinnedNews, setPinnedNews] = useState([]);
  const [rssNews, setRssNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editNews, setEditNews] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchNews = async () => {
    try {
      const [pinnedRes, publicRes] = await Promise.all([
        axios.get(`${API}/admin/news`, authHeader()),
        axios.get(`${API}/news`)
      ]);
      setPinnedNews(pinnedRes.data);
      // Filter out pinned from RSS
      const pinnedIds = new Set(pinnedRes.data.map(n => n.url));
      setRssNews(publicRes.data.filter(n => !pinnedIds.has(n.url) && !n.is_pinned));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const openCreate = () => {
    setEditNews(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowModal(true);
  };

  const openEdit = (news) => {
    setEditNews(news);
    setForm({
      title: news.title || "",
      description: news.description || "",
      url: news.url || "",
      image_url: news.image_url || "",
      source: news.source || "ShansoAI"
    });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.url) {
      setError("Title and URL are required");
      return;
    }
    
    setSaving(true);
    setError("");
    
    try {
      if (editNews) {
        await axios.put(`${API}/admin/news/${editNews.id}`, form, authHeader());
      } else {
        await axios.post(`${API}/admin/news`, form, authHeader());
      }
      setShowModal(false);
      fetchNews();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save news");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pinned news?")) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, authHeader());
      fetchNews();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const toggleActive = async (news) => {
    try {
      await axios.put(`${API}/admin/news/${news.id}`, { is_active: !news.is_active }, authHeader());
      fetchNews();
    } catch (err) {
      alert("Failed to update");
    }
  };

  const pinRssNews = (rssItem) => {
    setEditNews(null);
    setForm({
      title: rssItem.title || "",
      description: rssItem.description || "",
      url: rssItem.url || "",
      image_url: rssItem.image_url || "",
      source: rssItem.source || "Featured"
    });
    setError("");
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-56 bg-white border-r border-gray-200 z-50">
        <div className="p-5 border-b border-gray-200">
          <Link to="/" className="font-outfit font-black text-lg text-black">
            Shanso<span className="text-[#00F0FF]">AI</span>
          </Link>
          <div className="text-[10px] text-gray-400 font-dm-sans mt-0.5">Admin Panel</div>
        </div>
        <nav className="p-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = window.location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-dm-sans mb-1 transition-colors ${
                  isActive ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon size={16} />
                {item.label}
              </Link>
            );
          })}
          <div className="border-t border-gray-200 my-3" />
          <Link
            to="/admin/change-password"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-dm-sans text-gray-600 hover:bg-gray-100"
          >
            <KeyRound size={16} />
            Change Password
          </Link>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="ml-56 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-outfit font-bold text-2xl text-black">AI News Management</h1>
            <p className="text-gray-500 text-sm font-dm-sans">Pin featured news or let RSS auto-fetch</p>
          </div>
          <button
            onClick={openCreate}
            data-testid="create-news-btn"
            className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-dm-sans font-semibold hover:bg-gray-800 transition-colors"
          >
            <Plus size={16} /> Add Pinned News
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Pinned News */}
            <div>
              <h2 className="font-outfit font-bold text-lg text-black mb-4 flex items-center gap-2">
                <Pin size={18} className="text-[#00F0FF]" /> Pinned News ({pinnedNews.length})
              </h2>
              {pinnedNews.length === 0 ? (
                <div className="bg-white border border-gray-200 p-8 text-center text-gray-500 font-dm-sans">
                  No pinned news. Add your own or pin from RSS below.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pinnedNews.map((news) => (
                    <div
                      key={news.id}
                      className={`bg-white border ${news.is_active ? 'border-[#00F0FF]' : 'border-gray-200 opacity-60'} p-4 relative group`}
                    >
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(news)}
                          className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => toggleActive(news)}
                          className={`p-1.5 rounded ${news.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          title={news.is_active ? 'Active' : 'Inactive'}
                        >
                          <Pin size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(news.id)}
                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="text-[10px] text-[#00F0FF] font-bold uppercase mb-1">{news.source}</div>
                      <h3 className="font-outfit font-bold text-sm text-black mb-2 line-clamp-2">{news.title}</h3>
                      {news.description && (
                        <p className="text-gray-500 text-xs font-dm-sans line-clamp-2 mb-2">{news.description}</p>
                      )}
                      <a
                        href={news.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00F0FF] text-xs font-dm-sans flex items-center gap-1 hover:underline"
                      >
                        View <ExternalLink size={10} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RSS News */}
            <div>
              <h2 className="font-outfit font-bold text-lg text-black mb-4 flex items-center gap-2">
                <Newspaper size={18} className="text-gray-400" /> Auto-fetched RSS News ({rssNews.length})
              </h2>
              {rssNews.length === 0 ? (
                <div className="bg-white border border-gray-200 p-8 text-center text-gray-500 font-dm-sans">
                  No RSS news available.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {rssNews.slice(0, 8).map((news, i) => (
                    <div
                      key={news.id || i}
                      className="bg-white border border-gray-200 p-3 relative group"
                    >
                      <button
                        onClick={() => pinRssNews(news)}
                        className="absolute top-2 right-2 p-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Pin this news"
                      >
                        <Pin size={14} />
                      </button>
                      <div className="text-[10px] text-gray-400 font-dm-sans mb-1">{news.source}</div>
                      <h3 className="font-outfit font-semibold text-xs text-black mb-1 line-clamp-2">{news.title}</h3>
                      <a
                        href={news.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 text-[10px] font-dm-sans flex items-center gap-1 hover:text-[#00F0FF]"
                      >
                        View <ExternalLink size={10} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit font-bold text-lg">
              {editNews ? "Edit Pinned News" : "Add Pinned News"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded font-dm-sans">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Title *</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                placeholder="AI News Headline"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">URL *</label>
              <input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black resize-none"
                placeholder="Brief description..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Image URL</label>
                <input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Source</label>
                <input
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                  placeholder="ShansoAI"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 border border-gray-300 py-2.5 text-sm font-dm-sans font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-black text-white py-2.5 text-sm font-dm-sans font-semibold hover:bg-gray-800 transition-colors disabled:opacity-60"
              >
                {saving ? "Saving..." : editNews ? "Update" : "Add News"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
